import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChartBar, Users, Briefcase, CreditCard, Trash, Star, CurrencyEur } from "@phosphor-icons/react";

export default function Admin() {
  const { t } = useLang();
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);

  if (ready && (!user || !user.is_admin)) return <Navigate to="/" replace />;

  const delUser = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("OK"); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delJob = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/admin/jobs/${id}`); toast.success("OK"); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const feature = async (id) => {
    try { await api.post(`/admin/jobs/${id}/feature`); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const tabs = [
    ["overview", t("tab_overview"), <ChartBar size={18} />],
    ["users", t("tab_users"), <Users size={18} />],
    ["jobs", t("tab_jobs"), <Briefcase size={18} />],
    ["payments", t("tab_payments"), <CreditCard size={18} />],
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 py-8">
      <div className="flex items-center gap-3">
        <h1 className="font-head text-3xl font-bold tracking-tight">{t("admin_title")}</h1>
        <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-[#E11D2A] text-white">{t("admin")}</span>
      </div>

      <div className="mt-6 flex gap-1 border-b border-zinc-200">
        {tabs.map(([key, label, icon]) => (
          <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === key ? "border-[#E11D2A] text-[#E11D2A]" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            {icon} {label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <Stat label={t("stat_revenue")} value={`€${stats.revenue}`} icon={<CurrencyEur weight="bold" />} highlight />
          <Stat label={t("stat_users")} value={stats.users} sub={`${stats.guards} zzp · ${stats.companies} bedr.`} icon={<Users weight="duotone" />} />
          <Stat label={t("stat_jobs")} value={stats.jobs} sub={`${stats.open_jobs} ${t("open").toLowerCase()}`} icon={<Briefcase weight="duotone" />} />
          <Stat label={t("stat_applications")} value={stats.applications} icon={<Briefcase weight="duotone" />} />
          <Stat label={t("stat_unlocks")} value={stats.unlocks} icon={<Star weight="duotone" />} />
          <Stat label={t("stat_transactions")} value={stats.paid_transactions} icon={<CreditCard weight="duotone" />} />
          <div className="sm:col-span-3 bg-white border border-zinc-200 rounded-md p-5">
            <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3">{t("stat_revenue")} · {t("tab_payments")}</div>
            <div className="flex flex-wrap gap-6">
              {Object.entries(stats.revenue_by_purpose || {}).map(([k, v]) => (
                <div key={k}><div className="font-head font-black text-2xl text-[#E11D2A] tabular-nums">€{v}</div><div className="text-xs text-zinc-500">{k}</div></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "users" && (
        <Table head={[t("col_name"), t("col_email"), t("col_role"), t("col_plan"), t("col_actions")]}>
          {users.map((u) => (
            <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-zinc-100">
              <Td>{u.name} {u.is_admin && <span className="text-[10px] font-bold text-[#E11D2A]">★</span>}</Td>
              <Td className="text-zinc-500">{u.email}</Td>
              <Td>{u.role}</Td>
              <Td>{u.plan}</Td>
              <Td>{!u.is_admin && <button data-testid={`del-user-${u.id}`} onClick={() => delUser(u.id)} className="text-red-600 hover:text-red-700"><Trash size={18} /></button>}</Td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "jobs" && (
        <Table head={[t("col_name"), t("location"), t("pay"), t("col_status"), t("applicants"), t("col_actions")]}>
          {jobs.map((j) => (
            <tr key={j.id} data-testid={`admin-job-${j.id}`} className="border-t border-zinc-100">
              <Td>{j.featured && <Star size={13} weight="fill" className="text-[#FFCC00] inline mr-1" />}{j.title}</Td>
              <Td className="text-zinc-500">{j.location_name}{j.country ? `, ${j.country}` : ""}</Td>
              <Td className="tabular-nums">€{j.pay}</Td>
              <Td>{t(j.status) || j.status}</Td>
              <Td className="tabular-nums">{j.applications_count}</Td>
              <Td>
                <div className="flex gap-3">
                  <button data-testid={`feature-job-${j.id}`} onClick={() => feature(j.id)} title={t("action_feature")} className={j.featured ? "text-[#FFCC00]" : "text-zinc-400 hover:text-[#FFCC00]"}><Star size={18} weight={j.featured ? "fill" : "regular"} /></button>
                  <button data-testid={`del-job-${j.id}`} onClick={() => delJob(j.id)} className="text-red-600 hover:text-red-700"><Trash size={18} /></button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {tab === "payments" && (
        <Table head={[t("col_email"), "Type", t("col_amount"), t("col_status"), t("col_date")]}>
          {payments.map((p, i) => (
            <tr key={p.session_id || i} data-testid={`admin-payment-${i}`} className="border-t border-zinc-100">
              <Td className="text-zinc-500">{p.user_email || "—"}</Td>
              <Td>{p.purpose}</Td>
              <Td className="tabular-nums">€{p.amount}</Td>
              <Td><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>{p.payment_status}</span></Td>
              <Td className="text-zinc-500 text-xs">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</Td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

const Stat = ({ label, value, sub, icon, highlight }) => (
  <div className={`rounded-md border p-5 ${highlight ? "bg-[#E11D2A] text-white border-[#E11D2A]" : "bg-white border-zinc-200"}`}>
    <div className="flex items-center justify-between">
      <div className={`text-xs uppercase tracking-[0.15em] ${highlight ? "text-white/70" : "text-zinc-400"}`}>{label}</div>
      <span className={highlight ? "text-white/80" : "text-[#E11D2A]"}>{icon}</span>
    </div>
    <div className="font-head font-black text-3xl tabular-nums mt-2">{value}</div>
    {sub && <div className={`text-xs mt-1 ${highlight ? "text-white/70" : "text-zinc-500"}`}>{sub}</div>}
  </div>
);

const Table = ({ head, children }) => (
  <div className="mt-6 bg-white border border-zinc-200 rounded-md overflow-x-auto">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-400">
        {head.map((h, i) => <th key={i} className="px-4 py-3 font-semibold">{h}</th>)}
      </tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
